import {
  createHashRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router-dom';
import useFeatures from 'hooks/useFeatures';
import Login from 'pages/login/Login';
import ForgotPassword from 'pages/forgotPassword/ForgotPassword';
import {
  LEGACY_PAGES_PRIVATE,
  LEGACY_PAGES_PUBLIC,
} from 'pages/legacy/legacyPages.constants';
import LegacyPage from 'pages/legacy/LegacyPage';
import PromoCodes from 'pages/promoCodes/PromoCodes';
import Resolutions from 'pages/channels/airbnb/resolutions/Resolutions';
import AirbnbNotifications from 'pages/channels/airbnb/notifications/AirbnbNotifications';
import AddPromoCode from 'pages/promoCodes/add/AddPromoCode';
import Inbox from 'pages/inbox/Inbox';
import ThirdPartyAgency from 'pages/thirdPartyAgency/ThirdPartyAgency';
import EditThirdPartyAgency from 'pages/thirdPartyAgencyManage/editThirdPartyAgency/EditThirdPartyAgency';
import AddThirdPartyAgency from 'pages/thirdPartyAgencyManage/addThirdPartyAgency/AddThirdPartyAgency';
import Logout from 'pages/logout/Logout';
import LegacyRedirect from 'pages/legacy/LegacyRedirect';
import Statements from 'pages/statements/Statements';
import OwnerAccount from 'pages/ownerAccount/OwnerAccount';
import Properties from 'pages/properties/Properties';
import ExchangeToken from 'pages/exchangeToken/ExchangeToken';
import Authorization from 'pages/authorization/Authorization';
import Analytics from 'pages/analyticsAndHostQuality/Analytics';
import Dashboard from 'pages/dashboard/Dashboard';
import Refer from 'pages/refer/Refer';
import Account from 'pages/account/Account';
import OwnerAdjustments from 'pages/ownerAdjustments/OwnerAdjustments';
import Pipeline from 'pages/pipeline/Pipeline';
import NotFound from 'pages/notFound/NotFound';
import AgencyProperties from 'pages/agencyProperties/Properties';
import TurnoManagement from 'pages/turnoManagement/TurnoManagement';
import OrderView from 'pages/orderView/OrderView';
import OrderViewLegacyAdapter from 'pages/orderView/OrderViewLegacyAdapter';
import AddMultiUnitProperty from 'pages/property/add/multiUnit/AddMultiUnitProperty';
import AddSingleProperty from 'pages/property/add/single/AddSingleProperty';
import AddUnitType from 'pages/property/add/unitType/AddUnitType';
import CompleteRegistration from 'pages/onboarding/CompleteRegistration';
import Property from 'pages/property/Property';
import PropertySettingsAmenitiesTab from 'pages/property/amenities/PropertySettingsAmenitiesTab';
import PropertySettingsChannelsTab from 'pages/property/channels/PropertySettingsChannelsTab';
import PropertySettingsCustomDataTab from 'pages/property/customData/PropertySettingsCustomDataTab';
import PropertySettingsDescriptionsTab from 'pages/property/descriptions/PropertySettingsDescriptionsTab';
import PropertySettingsFeesAndPoliciesTab from 'pages/property/feesAndPolicies/PropertySettingsFeesAndPoliciesTab';
import PropertySettingsKpisTab from 'pages/property/kpis/PropertySettingsKpisTab';
import PropertySettingsMainTab from 'pages/property/mainSettings/PropertySettingsMainTab';
import PropertySettingsOwnersTab from 'pages/property/owners/PropertySettingsOwnersTab';
import PropertySettingsPhotosTab from 'pages/property/photos/PropertySettingsPhotosTab';
import PropertySettingsPricingTab from 'pages/property/pricing/PropertySettingsPricingTab';
import PropertySettingsReviewsTab from 'pages/property/reviews/PropertySettingsReviewsTab';
import PropertySettingsServiceProvidersTab from 'pages/property/serviceProviders/PropertySettingsServiceProvidersTab';
import PropertySettingsUnitTypesTab from 'pages/property/unitTypes/PropertySettingsUnitTypesTab';
import PropertySettingsUnitsTab from 'pages/property/units/PropertySettingsUnitsTab';
import PropertySettingsRentalConditionsTab from 'pages/property/rentalConditions/PropertySettingsRentalConditionsTab';
import { PropertySettingsTab } from 'pages/property/PropertySettingsTabs.constants';
import AiHub from 'pages/aiHub/AiHub';
import Automations from 'pages/automations/Automations';
import { AutomationTabDefaultRoute } from 'pages/automations/AutomationsTabs';
import { AutomationsTab } from 'pages/automations/Automations.constants';
import SystemTemplatesTab from 'pages/automations/systemTemplates/SystemTemplatesTab';
import SystemTemplatesTabModal from 'pages/automations/systemTemplates/SystemTemplatesTabModal';
import CustomTemplatesTab from 'pages/automations/customTemplates/CustomTemplatesTab';
import CustomTemplatesTabModal from 'pages/automations/customTemplates/CustomTemplatesTabModal';
import TriggersTab from 'pages/automations/triggers/TriggersTab';
import TriggersTabModal from 'pages/automations/triggers/TriggersTabModal';
import CustomFieldsTab from 'pages/automations/customFields/CustomFieldsTab';
import ReviewsTab from 'pages/automations/reviews/ReviewsTab';
import PreArrivalFormTab from 'pages/automations/preArrivalForm/PreArrivalFormTab';
import ScheduledMessagesTab from 'pages/automations/scheduledMessages/ScheduledMessagesTab';
import MessageLogTab from 'pages/automations/messageLog/MessageLogTab';
import CalendarToolsTab from 'pages/automations/calendarTools/CalendarToolsTab';
import DirectChannelsTab from 'pages/channels/directChannels/DirectChannelsTab';
import ChannelManagersTab from 'pages/channels/channelManagers/ChannelManagersTab';
import Channels from 'pages/channels/Channels';
import ChannelDetails from 'pages/channels/channelDetails/ChannelDetails';
import { ChannelsDefaultRoute } from 'pages/channels/ChannelsTabs';
import Integrations from 'pages/integrations/Integrations';
import { IntegrationsDefaultRoute } from 'pages/integrations/IntegrationsTabs';
import IntegrationsAllTab from 'pages/integrations/tabs/all/IntegrationsAllTab';
import IntegrationsFeaturedTab from 'pages/integrations/tabs/featured/IntegrationsFeaturedTab';
import IntegrationsPaymentsTab from 'pages/integrations/tabs/payments/IntegrationsPaymentProcessorsTab';
import IntegrationsDynamicPricingToolTab from 'pages/integrations/tabs/dynamicPricingTool/IntegrationsDynamicPricingTab';
import IntegrationsAccountingTab from 'pages/integrations/tabs/accounting/IntegrationsAccountingTab';
import IntegrationsDigitalSignatureTab from 'pages/integrations/tabs/digitalSignature/IntegrationsDigitalSignatureTab';
import IntegrationsHomeAutomationTab from 'pages/integrations/tabs/homeAutomation/IntegrationsHomeAutomationTab';
import IntegrationsListingToolsTab from 'pages/integrations/tabs/listingTools/IntegrationsListingToolsTab';
import IntegrationsReviewsTab from 'pages/integrations/tabs/reviews/IntegrationsReviewsTab';
import IntegrationsInsuranceAndScreeningTab from 'pages/integrations/tabs/insuranceAndScreening/IntegrationsInsuranceAndScreeningTab';
import IntegrationDetails from 'pages/integrations/integrationDetails/IntegrationDetails';
import RescheduleScheduledMessageModal from 'pages/automations/scheduledMessages/RescheduleScheduledMessageModal';
import ZapierTemplates from 'pages/integrations/zapierTemplates/ZapierTemplates';
import ImageCompressionTab from 'pages/integrations/tabs/imageCompression/ImageCompressionTab';
import ApplicationIntegrationTab from 'pages/integrations/tabs/workflowAutomations/WorkflowAutomationsTab';
import ComplianceAndRegulationsTab from 'pages/integrations/tabs/complianceAndRegulations/ComplianceAndRegulationsTab';
import IntegrationConnection from 'pages/integrations/IntegrationConnection/IntegrationConnection';
import IntegrationsGuestExperienceTab from 'pages/integrations/tabs/guestExperience/IntegrationsGuestExperienceTab';
import IntegrationsCustomWebsitesTab from 'pages/integrations/tabs/customWebsites/IntegrationsCustomWebsitesTab';
import IntegrationsBrandManagementTab from 'pages/integrations/tabs/brandManagement/IntegrationsBrandManagementTab';
import IntegrationsCleaningAndTurnoverTab from 'pages/integrations/tabs/cleaningAndTurnover/IntegrationsCleaningAndTurnoverTab';
import IntegrationsCommunicationsTab from 'pages/integrations/tabs/workflowAutomations/IntegrationsCommunicationsTab';
import { IntegrationsTab } from 'pages/integrations/IntegrationsTabs.constants';
import IntegrationsAnalyticsToolsTab from 'pages/integrations/tabs/analyticsTools/IntegrationsAnalyticsToolsTab';
import DirectBookingEngine, {
  DirectBookingEngineDefaultRoute,
} from 'pages/directBookingEngine/DirectBookingEngine';
import {
  DirectBookingEngineTab,
  WebsiteSettingsModalPath,
} from 'pages/directBookingEngine/DirectBookingEngineTabs.constants';
import WebsiteSettingsTab from 'pages/directBookingEngine/websiteSettings/WebsiteSettingsTab';
import WebsiteSettingsModalRoute from 'pages/directBookingEngine/websiteSettings/WebsiteSettingsModalRoute';
import ColorSettingsModal, {
  colorSettingsModalId,
} from 'pages/directBookingEngine/websiteSettings/sections/colorSettings/ColorSettingsModal';
import NavigationSettingsModal from 'pages/directBookingEngine/websiteSettings/sections/navigationSettings/NavigationSettingsModal';
import { navigationSettingsModalId } from 'pages/directBookingEngine/websiteSettings/sections/navigationSettings/NavigationSettings.constants';
import WhiteLabelSettingsTab from 'pages/directBookingEngine/whiteLabelSettings/WhiteLabelSettingsTab';
import ScreenAndProtect from 'pages/screenAndProtect/ScreenAndProtect';
import ScreenAndProtectIndex from 'pages/screenAndProtect/ScreenAndProtectIndex';
import ScreenAndProtectDetails from 'pages/screenAndProtect/details/ScreenAndProtectDetails';
import ScreenAndProtectCurrent from 'pages/screenAndProtect/current/ScreenAndProtectCurrent';
import ScheduledMessagesModal from 'pages/automations/scheduledMessages/ScheduledMessagesModal';
import MessageLogModal from 'pages/automations/messageLog/MessageLogModal';
import HostfullyAccounting from 'pages/hostfullyAccounting/HostfullyAccounting';
import HostfullyAccountingMarketing from 'pages/hostfullyAccounting/marketing/HostfullyAccountingMarketing';
import HostfullyAssist from 'pages/hostfullyAssist/HostfullyAssist';
import HostfullyAssistMarketing from 'pages/hostfullyAssist/marketing/HostfullyAssistMarketing';
import HostfullyAssistDetails from 'pages/hostfullyAssist/details/HostfullyAssistDetails';
import { HOSTFULLY_ASSIST_PATH } from 'pages/hostfullyAssist/HostfullyAssist.constants';
import StackedCalendar from 'pages/virtualizedStackedCalendar/StackedCalendar';
import PropertySettingsSeasonalityTab from 'pages/property/seasonality/PropertySettingsSeasonalityTab';
import PropertySettingsSeasonalityTabModal from 'pages/property/seasonality/PropertySettingsSeasonalityTabModal';
import RoutesWrapper from './RoutesWrapper';
import PublicRoute from './PublicRoute';
import PrivateRoute from './PrivateRoute';

const migratedPagesProvider = {
  CALENDAR: () => <StackedCalendar />,
  DASHBOARD: () => <Dashboard />,
  PIPELINE: () => <Pipeline />,
  // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  PROPERTIES: ({ allowPropertiesWebapp }) =>
    allowPropertiesWebapp && <AgencyProperties />,
  // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  ORDER_VIEW: ({ allowOrderViewWebapp }) =>
    allowOrderViewWebapp && <OrderViewLegacyAdapter />,
};

const Router = () => {
  const featureFlags = useFeatures();

  const router = createHashRouter(
    createRoutesFromElements(
      <Route path="/" element={<RoutesWrapper />}>
        <Route path="/r/:redirectUrl" element={<LegacyRedirect />} />
        {/* logout must not be a private route as it'd break redirection to requested path */}
        <Route path="/logout" element={<Logout />} />

        {/* Private Routes */}
        <Route path="/" element={<PrivateRoute />}>
          <Route path="/l/:pageUrl" element={<LegacyPage />} />
          {LEGACY_PAGES_PRIVATE.map(({ pageId, path }) => (
            <Route
              key={pageId}
              path={`/${path}`}
              element={
                // @ts-expect-error TS7053 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
                migratedPagesProvider[pageId]?.(featureFlags) || (
                  <LegacyPage pageId={pageId} />
                )
              }
            />
          ))}
          <Route path="/channels/airbnb/">
            <Route path="resolutions" element={<Resolutions />} />
            <Route path="notifications" element={<AirbnbNotifications />} />
          </Route>

          <Route path="/promo-codes">
            <Route index element={<PromoCodes />} />
            <Route path="add" element={<AddPromoCode />} />
          </Route>
          <Route path="/third-party-agency">
            <Route index element={<ThirdPartyAgency />} />
            <Route path="add" element={<AddThirdPartyAgency />} />
            <Route path=":id" element={<EditThirdPartyAgency />} />
          </Route>
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/refer" element={<Refer />} />
          <Route path="/account" element={<Account />} />
          <Route
            path="/owner-adjustments/:propertyUid"
            element={<OwnerAdjustments />}
          />
          <Route path="/properties-migrated" element={<AgencyProperties />} />
          <Route path="/order/:uid" element={<OrderView />} />
          <Route path="/turnover-management" element={<TurnoManagement />} />

          <Route path="/property">
            <Route path="add">
              <Route path="multi-unit" element={<AddMultiUnitProperty />} />
              <Route path="single" element={<AddSingleProperty />} />
            </Route>
            <Route path=":parentPropertyUid/add">
              <Route path="unit-type" element={<AddUnitType />} />
            </Route>
            <Route path=":uid" element={<Property />}>
              <Route index element={<PropertySettingsMainTab />} />
              <Route
                path={PropertySettingsTab.amenities}
                element={<PropertySettingsAmenitiesTab />}
              />
              <Route
                path={PropertySettingsTab.channels}
                element={<PropertySettingsChannelsTab />}
              />
              <Route
                path={PropertySettingsTab.customData}
                element={<PropertySettingsCustomDataTab />}
              />
              <Route
                path={PropertySettingsTab.descriptions}
                element={<PropertySettingsDescriptionsTab />}
              />
              <Route
                path={PropertySettingsTab.feesAndPolicies}
                element={<PropertySettingsFeesAndPoliciesTab />}
              />
              <Route
                path={PropertySettingsTab.kpis}
                element={<PropertySettingsKpisTab />}
              />
              <Route
                path={PropertySettingsTab.owners}
                element={<PropertySettingsOwnersTab />}
              />
              <Route
                path={PropertySettingsTab.photos}
                element={<PropertySettingsPhotosTab />}
              />
              <Route
                path={PropertySettingsTab.pricing}
                element={<PropertySettingsPricingTab />}
              />
              <Route
                path={PropertySettingsTab.reviews}
                element={<PropertySettingsReviewsTab />}
              />
              <Route
                path={PropertySettingsTab.serviceProviders}
                element={<PropertySettingsServiceProvidersTab />}
              />
              <Route
                path={PropertySettingsTab.units}
                element={<PropertySettingsUnitsTab />}
              />
              <Route
                path={PropertySettingsTab.unitTypes}
                element={<PropertySettingsUnitTypesTab />}
              />
              <Route
                path={PropertySettingsTab.rentalConditions}
                element={<PropertySettingsRentalConditionsTab />}
              />
              <Route
                path={PropertySettingsTab.seasonality}
                element={<PropertySettingsSeasonalityTab />}
              >
                <Route
                  path=":seasonalRuleUid"
                  element={<PropertySettingsSeasonalityTabModal />}
                />
              </Route>
            </Route>
          </Route>

          <Route path="/automations-migrated" element={<Automations />}>
            <Route index element={<AutomationTabDefaultRoute />} />
            <Route
              path={AutomationsTab.systemTemplates}
              element={<SystemTemplatesTab />}
            >
              <Route path=":key" element={<SystemTemplatesTabModal />} />
            </Route>
            <Route
              path={AutomationsTab.customTemplates}
              element={<CustomTemplatesTab />}
            >
              <Route path=":key" element={<CustomTemplatesTabModal />} />
            </Route>
            <Route path={AutomationsTab.triggers} element={<TriggersTab />}>
              <Route path=":key" element={<TriggersTabModal />} />
            </Route>
            <Route
              path={AutomationsTab.customFields}
              element={<CustomFieldsTab />}
            />
            <Route path={AutomationsTab.reviews} element={<ReviewsTab />} />
            <Route
              path={AutomationsTab.preArrivalForm}
              element={<PreArrivalFormTab />}
            />
            <Route
              path={AutomationsTab.scheduledMessages}
              element={<ScheduledMessagesTab />}
            >
              <Route path=":key" element={<ScheduledMessagesModal />} />
              <Route
                path=":key/reschedule"
                element={<RescheduleScheduledMessageModal />}
              />
            </Route>
            <Route path={AutomationsTab.messageLog} element={<MessageLogTab />}>
              <Route path=":key" element={<MessageLogModal />} />
            </Route>
            <Route
              path={AutomationsTab.calendarTools}
              element={<CalendarToolsTab />}
            />
          </Route>

          <Route path="/channels">
            <Route element={<Channels />}>
              <Route index element={<ChannelsDefaultRoute />} />
              <Route path="direct-channels" element={<DirectChannelsTab />} />
              <Route path="channel-managers" element={<ChannelManagersTab />} />
            </Route>

            <Route path="details/:id" element={<ChannelDetails />} />
          </Route>

          <Route path="/integrations">
            <Route element={<Integrations />}>
              <Route index element={<IntegrationsDefaultRoute />} />
              <Route
                path={IntegrationsTab.all}
                element={<IntegrationsAllTab />}
              />
              <Route
                path={IntegrationsTab.featured}
                element={<IntegrationsFeaturedTab />}
              />
              <Route
                path={IntegrationsTab.analyticsTools}
                element={<IntegrationsAnalyticsToolsTab />}
              />
              <Route
                path={IntegrationsTab.payments}
                element={<IntegrationsPaymentsTab />}
              />
              <Route
                path={IntegrationsTab.dynamicPricing}
                element={<IntegrationsDynamicPricingToolTab />}
              />
              <Route
                path={IntegrationsTab.cleaningAndTurnover}
                element={<IntegrationsCleaningAndTurnoverTab />}
              />
              <Route
                path={IntegrationsTab.accounting}
                element={<IntegrationsAccountingTab />}
              />
              <Route
                path={IntegrationsTab.insuranceAndScreening}
                element={<IntegrationsInsuranceAndScreeningTab />}
              />
              <Route
                path={IntegrationsTab.digitalSignatures}
                element={<IntegrationsDigitalSignatureTab />}
              />
              <Route
                path={IntegrationsTab.homeAutomation}
                element={<IntegrationsHomeAutomationTab />}
              />
              <Route
                path={IntegrationsTab.customWebsites}
                element={<IntegrationsCustomWebsitesTab />}
              />
              <Route
                path={IntegrationsTab.brandManagement}
                element={<IntegrationsBrandManagementTab />}
              />
              <Route
                path={IntegrationsTab.listingTools}
                element={<IntegrationsListingToolsTab />}
              />
              <Route
                path={IntegrationsTab.guestExperience}
                element={<IntegrationsGuestExperienceTab />}
              />
              <Route
                path={IntegrationsTab.reviews}
                element={<IntegrationsReviewsTab />}
              />
              <Route
                path={IntegrationsTab.imageCompression}
                element={<ImageCompressionTab />}
              />
              <Route
                path={IntegrationsTab.workflowAutomations}
                element={<ApplicationIntegrationTab />}
              />
              <Route
                path={IntegrationsTab.communications}
                element={<IntegrationsCommunicationsTab />}
              />
              <Route
                path={IntegrationsTab.complianceAndRegulations}
                element={<ComplianceAndRegulationsTab />}
              />
            </Route>
            <Route path="details/:id" element={<IntegrationDetails />} />
            <Route
              path="details/zapier/templates"
              element={<ZapierTemplates />}
            />
            <Route path="connect/:id" element={<IntegrationConnection />} />
          </Route>

          <Route
            path="/direct-booking-engine"
            element={<DirectBookingEngine />}
          >
            <Route index element={<DirectBookingEngineDefaultRoute />} />
            <Route
              path={DirectBookingEngineTab.websiteSettings}
              element={<WebsiteSettingsTab />}
            >
              <Route
                path={WebsiteSettingsModalPath.navigation}
                element={
                  <WebsiteSettingsModalRoute
                    modal={NavigationSettingsModal}
                    modalId={navigationSettingsModalId}
                  />
                }
              />
              <Route
                path={WebsiteSettingsModalPath.colors}
                element={
                  <WebsiteSettingsModalRoute
                    modal={ColorSettingsModal}
                    modalId={colorSettingsModalId}
                  />
                }
              />
            </Route>
            <Route
              path={DirectBookingEngineTab.customDomain}
              element={<WhiteLabelSettingsTab />}
            />
          </Route>

          <Route path="/screen-and-protect" element={<ScreenAndProtect />}>
            <Route index element={<ScreenAndProtectIndex />} />
            <Route path="details" element={<ScreenAndProtectDetails />} />
            <Route path="current" element={<ScreenAndProtectCurrent />} />
          </Route>

          <Route path="/hostfully-accounting" element={<HostfullyAccounting />}>
            <Route index element={<HostfullyAccountingMarketing />} />
          </Route>

          <Route path={HOSTFULLY_ASSIST_PATH} element={<HostfullyAssist />}>
            <Route index element={<HostfullyAssistMarketing />} />
            <Route path="details" element={<HostfullyAssistDetails />} />
          </Route>

          {/* Hostbuddy routes */}
          <Route path="ai-hub" element={<AiHub />} />

          {/* guest */}
          <Route path="/guest">
            <Route path="inbox" element={<Inbox />} />
          </Route>

          {/* owner */}
          <Route path="/owner">
            <Route path="analytics" element={<Analytics />} />
            <Route path="inbox" element={<Inbox />} />
            <Route path="calendar" element={<StackedCalendar />} />
            <Route path="account" element={<OwnerAccount />} />
            <Route path="statements" element={<Statements />} />
            <Route path="properties" element={<Properties />} />
          </Route>

          <Route path="/oauth/authorize" element={<Authorization />} />
        </Route>

        {/* Public Routes */}
        <Route path="/" element={<PublicRoute />}>
          {LEGACY_PAGES_PUBLIC.map(({ pageId, path }) => (
            <Route
              key={pageId}
              path={`/${path}`}
              element={<LegacyPage pageId={pageId} />}
            />
          ))}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/exchange-token" element={<ExchangeToken />} />
          <Route path="/register/confirm" element={<CompleteRegistration />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>,
    ),
  );

  return <RouterProvider router={router} />;
};

export default Router;
